import type { AiAgentRunPage, AiAgentRunStatus } from '@livechat/shared';
import { useCallback, useEffect, useState } from 'react';

import { listAiAgentRuns } from '../api.js';
import { useDateFormat } from '../date-format.js';

const PAGE_SIZE = 10;
const EMPTY_PAGE: AiAgentRunPage = { items: [], page: 1, pageSize: PAGE_SIZE, total: 0 };

function duration(value?: number): string {
	if (value === undefined) return '—';
	return value < 1_000 ? `${value} ms` : `${(value / 1_000).toFixed(1)} s`;
}

function statusLabel(status: AiAgentRunStatus): string {
	return status === 'handed_off'
		? 'Handed off'
		: `${status.charAt(0).toUpperCase()}${status.slice(1)}`;
}

function detailLabel(value?: string): string | undefined {
	return value?.replaceAll('_', ' ');
}

function safeExternalUrl(value?: string): string | undefined {
	if (!value) return undefined;
	try {
		const url = new URL(value);
		return ['http:', 'https:'].includes(url.protocol) ? url.toString() : undefined;
	} catch {
		return undefined;
	}
}

export function AiAgentActivity({ tenantId }: { tenantId: string }) {
	const { formatDateTime } = useDateFormat();
	const [activity, setActivity] = useState<AiAgentRunPage>(EMPTY_PAGE);
	const [page, setPage] = useState(1);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string>();

	const load = useCallback(
		async (targetPage: number, silent = false): Promise<void> => {
			if (!silent) setLoading(true);
			try {
				const loaded = await listAiAgentRuns(tenantId, {
					page: targetPage,
					pageSize: PAGE_SIZE,
				});
				const lastPage = Math.max(1, Math.ceil(loaded.total / loaded.pageSize));
				if (targetPage > lastPage) {
					setPage(lastPage);
					return;
				}
				setActivity(loaded);
				if (!silent) setError(undefined);
			} catch (caught) {
				if (!silent) {
					setError(
						caught instanceof Error ? caught.message : 'Could not load AI activity',
					);
				}
			} finally {
				if (!silent) setLoading(false);
			}
		},
		[tenantId],
	);

	useEffect(() => {
		void load(page);
		const interval = window.setInterval(() => void load(page, true), 10_000);
		return () => window.clearInterval(interval);
	}, [load, page]);

	const pageCount = Math.max(1, Math.ceil(activity.total / activity.pageSize));
	const first = activity.total ? (page - 1) * activity.pageSize + 1 : 0;
	const last = Math.min(page * activity.pageSize, activity.total);

	return (
		<section className="ai-activity-section">
			<header className="table-section-heading">
				<div>
					<h3>AI Activity</h3>
				</div>
				<span className="table-section-count">{activity.total} runs</span>
			</header>
			{error && <div className="alert error">{error}</div>}
			<div className="table-card paginated-table">
				<div
					aria-busy={loading}
					className="ai-activity-table paginated-table-scroll"
					role="table"
				>
					<div
						className="ai-activity-row ai-activity-head standard-table-head"
						role="row"
					>
						<div role="columnheader">Started</div>
						<div role="columnheader">AI agent</div>
						<div role="columnheader">Model</div>
						<div role="columnheader">Outcome</div>
						<div role="columnheader">Knowledge</div>
						<div role="columnheader">Tokens</div>
						<div role="columnheader">Duration</div>
					</div>
					{activity.items.map((run) => {
						const detail = detailLabel(
							run.guardrailCode ?? run.errorCode ?? run.handoffReason,
						);
						return (
							<div className="table-row ai-activity-row" key={run.id} role="row">
								<div role="cell">{formatDateTime(run.startedAt)}</div>
								<div className="ai-activity-agent" role="cell">
									<strong>{run.agentName}</strong>
									<span>
										{run.attemptCount} attempt
										{run.attemptCount === 1 ? '' : 's'}
									</span>
								</div>
								<div className="ai-activity-model" role="cell">
									<strong>{run.model}</strong>
									<span>{run.provider}</span>
									{run.tenantConfigVersion !== undefined && (
										<small>
											Company v{run.tenantConfigVersion} · Platform v
											{run.platformConfigVersion ?? '—'}
										</small>
									)}
								</div>
								<div className="ai-activity-outcome" role="cell">
									<span className={`ai-run-status ai-run-${run.status}`}>
										{run.guardrailAction === 'clarify'
											? 'Clarified'
											: statusLabel(run.status)}
									</span>
									{detail && <small>{detail}</small>}
									{run.guardrailAction && (
										<small>Guardrail · {run.guardrailAction}</small>
									)}
								</div>
								<div role="cell">
									{run.knowledgeSources.length ? (
										<details className="ai-run-sources">
											<summary>{run.knowledgeChunkCount} chunks</summary>
											<ol>
												{run.knowledgeSources.map((source) => {
													const href = safeExternalUrl(source.sourceUrl);
													return (
														<li
															key={`${source.rank}-${source.sourceId ?? source.sourceTitle}`}
														>
															{href ? (
																<a
																	href={href}
																	rel="noreferrer"
																	target="_blank"
																>
																	{source.sourceTitle}
																</a>
															) : (
																source.sourceTitle
															)}
															<small>
																chunk {source.chunkIndex + 1} ·
																score{' '}
																{source.relevanceScore.toFixed(3)}
															</small>
														</li>
													);
												})}
											</ol>
										</details>
									) : run.topRelevanceScore === undefined ? (
										'—'
									) : (
										<span>Top score {run.topRelevanceScore.toFixed(3)}</span>
									)}
								</div>
								<div role="cell">{run.totalTokens?.toLocaleString() ?? '—'}</div>
								<div role="cell">{duration(run.durationMs)}</div>
							</div>
						);
					})}
					{!loading && !activity.items.length && (
						<div className="ai-activity-empty" role="row">
							<div role="cell">
								<strong>No AI activity yet</strong>
								<span>
									AI agent replies will appear here with their operational trace.
								</span>
							</div>
						</div>
					)}
					{loading && !activity.items.length && (
						<div className="ai-activity-empty" role="row">
							<div role="cell">Loading AI activity…</div>
						</div>
					)}
				</div>
				<footer className="data-pagination">
					<span>
						{first}–{last} of {activity.total} runs
					</span>
					<div>
						<button
							aria-label="Previous page"
							disabled={page <= 1 || loading}
							onClick={() => setPage((current) => Math.max(1, current - 1))}
							type="button"
						>
							‹
						</button>
						<span aria-live="polite">
							Page {page} of {pageCount}
						</span>
						<button
							aria-label="Next page"
							disabled={page >= pageCount || loading}
							onClick={() => setPage((current) => Math.min(pageCount, current + 1))}
							type="button"
						>
							›
						</button>
					</div>
				</footer>
			</div>
		</section>
	);
}
